import React, { useState } from 'react'
import {
  Box,
  TextField,
  FormHelperText,
  Button,
  Grid,
  CircularProgress,
  Typography,
  Autocomplete,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import axios from 'axios'
import { usePostApplication, useGetCount } from '../../../api/tng-campaign'
import { useSnackbar } from 'notistack'
import UploadIcon from '@mui/icons-material/Upload'
import { useNavigate } from 'react-router-dom'
import { workshop } from './workshop'
import { pattern } from './pattern'
import { size } from './size'

const quantity = ['2', '3', '4']
export const ApplicationForm = () => {
  const { register, handleSubmit, control, setValue, watch } = useForm({
    mode: 'onChange',
  })
  const { data = [] } = useGetCount()

  const { mutateAsync, isLoading } = usePostApplication()
  const { enqueueSnackbar } = useSnackbar()

  const navigate = useNavigate()

  const [selectedSize, setSelectedSize] = useState(null)
  const [submitIsLoading, setSubmitIsLoading] = useState(false)
  const [uploadError, setUploadError] = useState('')

  const onSubmit = async (data) => {
    setSubmitIsLoading(true)
    const file = data?.invoice?.[0]

    const formData = new FormData()
    formData.append('htperformance', file, file?.name ?? '')

    const uploadRes = await axios({
      url: `/api/upload/htperformance-invoice`,
      method: 'POST',
      data: formData,
    })
    const { filename, uploadPath } = uploadRes.data
    if (!filename || !uploadPath) {
      enqueueSnackbar('Error uploading file, please try again', {
        variant: 'error',
      })
      setSubmitIsLoading(false)
      return
    }
    const res = await mutateAsync({
      ...data,
      invoiceImg: uploadRes.data,
    }).catch((err) => {
      enqueueSnackbar(err?.response?.data?.message ?? 'Submit fail', {
        variant: 'error',
      })
    })
    if (res?.status === 200) {
      enqueueSnackbar('Application sent.', {
        variant: 'success',
      })
      navigate('/nexentiretngcampaign')
    }
    setSubmitIsLoading(false)
    return res
  }

  return (
    <Box sx={{ px: { xs: 3, sm: 10, md: 18 }, pb: 5 }}>
      <Box display='flex' justifyContent='flex-end' my={5}>
        <Button component='a' href='/nexentiretngcampaign' variant='outlined' size='small'>
          Back
        </Button>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={5}>
          <Grid item xs={12}>
            <Controller
              name='name'
              control={control}
              defaultValue=''
              rules={{
                required: 'Required',
                maxLength: { value: 35, message: 'Exceed word count' },
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  label='Name / Nama / 名字'
                  fullWidth
                  variant='standard'
                  value={value}
                  onChange={onChange}
                  error={!!error}
                  helperText={error ? error.message : null}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name='ic'
              control={control}
              defaultValue=''
              rules={{
                required: 'Required',
                maxLength: { value: 35, message: 'Exceed word count' },
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  label='Identity Card Number / Passport Number, No. Kad Pengenalan / Nombor Passport, 身份证号码 / 护照号码'
                  fullWidth
                  variant='standard'
                  value={value}
                  onChange={onChange}
                  error={!!error}
                  helperText={error ? error.message : null}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name='phone'
              control={control}
              defaultValue=''
              rules={{
                required: 'Required',
                pattern: {
                  value: /^(601)[0-46-9]-*[0-9]{7,8}$/,
                  message: `Invalid phone number. Phone number should follow the format of 601xxxxxxxx. '+' sign is not required.`,
                },
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  label='Phone Number / Telefon Bimbit / 电话号码'
                  variant='standard'
                  placeholder='Eg. 60123456789'
                  value={value}
                  fullWidth
                  onChange={onChange}
                  error={!!error}
                  helperText={error ? error.message : null}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name='email'
              control={control}
              defaultValue=''
              rules={{
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
                maxLength: { value: 50, message: 'Exceed word count' },
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  label='E-mail / E-mel / 电子邮件 (*Optional)'
                  variant='standard'
                  value={value}
                  fullWidth
                  onChange={onChange}
                  error={!!error}
                  helperText={error ? error.message : null}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name='pattern'
              control={control}
              rules={{
                required: 'Required',
              }}
              render={({ field: { onChange }, fieldState: { error } }) => (
                <Autocomplete
                  options={pattern}
                  onChange={(e, data) => onChange(data)}
                  defaultValue={''}
                  renderInput={(params) => (
                    <TextField
                      variant='standard'
                      error={!!error}
                      fullWidth
                      helperText={error ? error.message : null}
                      {...params}
                      label='Tyre Pattern / Corak Tyre / 轮胎类型'
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name='size'
              control={control}
              rules={{
                required: 'Required',
              }}
              render={({ field: { onChange }, fieldState: { error } }) => (
                <Autocomplete
                  options={size}
                  getOptionDisabled={(option) => !data.some((item) => item.size === option)}
                  onChange={(e, data) => {
                    onChange(data)
                    setSelectedSize(data)
                    setValue('quantity', '')
                  }}
                  defaultValue={''}
                  renderInput={(params) => (
                    <TextField
                      variant='standard'
                      error={!!error}
                      fullWidth
                      helperText={error ? error.message : null}
                      {...params}
                      label='Tyre Rim Size / Rim Tayar Size / 轮框尺寸'
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name='code'
              control={control}
              defaultValue=''
              rules={{
                required: 'Required',
                maxLength: { value: 50, message: 'Exceed word count' },
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  label='Tyre Size / Tayar Size / 轮胎尺寸'
                  placeholder='Eg. 255/60'
                  variant='standard'
                  value={value}
                  onChange={onChange}
                  error={!!error}
                  fullWidth
                  helperText={error ? error.message : null}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name='quantity'
              control={control}
              rules={{
                required: 'Required',
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Autocomplete
                  options={quantity}
                  getOptionDisabled={(option) =>
                    selectedSize &&
                    !data.some(
                      (item) =>
                        item.size === selectedSize && item.quantity === Math.floor(option / 2)
                    )
                  }
                  onChange={(e, data) => onChange(data)}
                  value={value ?? ''}
                  renderInput={(params) => (
                    <TextField
                      variant='standard'
                      error={!!error}
                      fullWidth
                      helperText={error ? error.message : null}
                      {...params}
                      label='Tyre Quantity / Kuantiti Tayar / 轮胎数量'
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name='workshop'
              control={control}
              rules={{
                required: 'Required',
              }}
              render={({ field: { onChange }, fieldState: { error } }) => (
                <Autocomplete
                  options={workshop}
                  onChange={(e, data) => onChange(data)}
                  defaultValue={''}
                  renderInput={(params) => (
                    <TextField
                      variant='standard'
                      error={!!error}
                      fullWidth
                      helperText={error ? error.message : null}
                      {...params}
                      label='Workshop Name / Nama Bengkel / 车厂名称 '
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} display='flex' flexDirection='column' justifyContent='center'>
            <Box>
              <Controller
                name='invoice'
                control={control}
                defaultValue=''
                rules={{
                  required: 'Required',
                }}
                render={({ field: { value }, fieldState: { error } }) => {
                  const props = register('invoice')
                  const combinedOnchange = (e) => {
                    const fileSize = e?.target?.files?.[0]?.size
                    const fileMb = fileSize / 1024 ** 2
                    if (fileMb > 5) {
                      setUploadError('File size must be within 5MB.')
                      setValue('invoice', null)
                    } else {
                      props.onChange(e)
                      setUploadError('')
                    }
                  }
                  return (
                    <>
                      <Button variant='outlined' startIcon={<UploadIcon />} component='label'>
                        Upload Proof of Purchase / Muat Naik Bukti Pembelian / 上传购买证明
                        <input
                          type='file'
                          accept='image/*'
                          hidden
                          {...props}
                          onChange={combinedOnchange}
                        />
                      </Button>
                      {error ? (
                        <FormHelperText error={true}>{error?.message}</FormHelperText>
                      ) : null}
                      {value ? <FormHelperText>{value[0]?.name}</FormHelperText> : null}
                      {/* throw error when file size is >2MB */}
                      {uploadError ? (
                        <FormHelperText error={true}>{uploadError}</FormHelperText>
                      ) : null}
                    </>
                  )
                }}
              />
            </Box>
          </Grid>
        </Grid>
        <Box my={2}>
          {[
            '*Please make sure your phone number is the same as your Touch’n Go e-Wallet account.',
            `*Redemption of one (1) / two (2) Touch'N Go reload pin number is strictly limited to one (1) contact number.`,
            '*Purchase Nexen Tire from participating dealers during the campaign period from 1th Aug 2024 to 30th September 2024.',
            `*The Nexen Tire purchased must be the dealership under HT Tyrehub Sdn Bhd. If the provided invoice is purchased from other dealership, it will be considered ineligible for the redemption of the Touch'N Go reload pin number.`,
          ].map((text, i) => (
            <Typography key={i} variant='h4' style={{ fontSize: 14 }}>
              {text}
            </Typography>
          ))}
          <Typography variant='h4' style={{ fontSize: 14 }}>
            *By submitting this form, you confirm that you have read, understood, and will respect{' '}
            <a
              style={{ color: '#002984' }}
              href={'/client-static/tngCampaign/TermsCondition.pdf'}
              target='_blank'
              rel='noopener noreferrer'
            >
              Terms & Conditions
            </a>{' '}
            and{' '}
            <a
              style={{ color: '#002984' }}
              href={'/client-static/tngCampaign/PersonalDataPrivacyNotice.pdf'}
              target='_blank'
              rel='noopener noreferrer'
            >
              Privacy Policy.
            </a>
          </Typography>
        </Box>
        <Box mt={5} display='flex' justifyContent='flex-end'>
          {/* <Button
            variant='contained'
            color='primary'
            type='submit'
            style={{ padding: '12px 32px' }}
            disabled={isLoading || submitIsLoading}
          >
            {(isLoading || submitIsLoading) && (
              <CircularProgress style={{ color: '#fff' }} size='1.5rem' />
            )}
            Submit
          </Button> */}
        </Box>
      </form>
    </Box>
  )
}
